/**
 * CABVersionHistory — Version history dropdown for CAB.
 *
 * Mirrors the TAP Design Preview version history UI:
 * - Header shows total versions including current
 * - Timeline with dots & connectors
 * - "Current Version" entry (always first, highlighted when no version selected)
 * - Historical version entries with "Revert to this version" button
 * - Selecting a historical version previews it; restoring deletes that
 *   snapshot and all newer ones.
 */

import React, { useState } from 'react';
import { Button as UIButton, Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Button } from '../Button/Button';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import type { CABVersion } from '../../services/cabApi';

interface CABVersionHistoryProps {
  versions: CABVersion[];
  selectedTimestampMs: number | null;
  onSelectVersion: (timestampMs: number) => void;
  onClearSelection: () => void;
  onRestoreVersion: (timestampMs: number) => Promise<void> | void;
  isRestoring?: boolean;
  permissions?: string[];
}

const formatVersionTimestamp = (timestampMs: number): string => {
  const date = new Date(timestampMs);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const CABVersionHistory: React.FC<CABVersionHistoryProps> = ({
  versions,
  selectedTimestampMs,
  onSelectVersion,
  onClearSelection,
  onRestoreVersion,
  isRestoring = false,
  permissions,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (versions.length === 0) return null;

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div className="relative">
        <Button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          variant="outline"
          size="very-small"
          className={`!w-8 !h-8 !p-0 !min-w-0 relative ${(selectedTimestampMs || isOpen) ? '!bg-bg-primary-hover' : ''}`}
          title="Version History"
          tooltipPosition="bottom"
          label={
            <>
              <Icon name={IconName.History} size="sm" inheritColor />
              {versions.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-utility-brand-500 text-text-white text-bg-v2-xs rounded-bg-v2-full w-4 h-4 flex items-center justify-center font-bg-v2-medium">
                  {versions.length}
                </span>
              )}
            </>
          }
        />

        {isOpen && (
          <div className="absolute right-0 mt-2 w-96 bg-bg-primary rounded-lg shadow-bg-v2-xl border border-border-secondary z-[60] max-h-[32rem] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-bg-v2-md border-b border-border-secondary bg-bg-primary-alt">
              <div className="flex items-center justify-between">
                <div>
                  <Text variant="text-md" weight="semibold" color="primary">
                    Version History
                  </Text>
                  <Text variant="text-xs" color="secondary" className="mt-0.5">
                    {versions.length + 1} version{versions.length !== 0 ? 's' : ''} available
                  </Text>
                </div>
                {selectedTimestampMs && (
                  <UIButton
                    onClick={onClearSelection}
                    variant="outline"
                    size="small"
                    className="!border-utility-brand-200 !text-utility-brand-800 hover:!bg-utility-brand-50"
                    label="View Current"
                  />
                )}
              </div>
            </div>

            {/* Version List */}
            <div className="flex-1 overflow-y-auto">
              {/* Current Version (always first) */}
              <div
                className={`px-bg-v2-lg py-bg-v2-md border-b border-border-secondary transition-colors ${!selectedTimestampMs
                  ? 'bg-utility-brand-100 border-l-4 border-l-utility-brand-500'
                  : 'hover:bg-bg-primary-hover border-l-4 border-l-transparent'
                  }`}
              >
                <div className="flex items-start gap-bg-v2-md">
                  <div className="flex flex-col items-center pt-1">
                    <div className={`w-3 h-3 rounded-full border-2 ${!selectedTimestampMs
                      ? 'bg-utility-brand-500 border-utility-brand-600'
                      : 'bg-bg-primary border-border-primary'
                      }`} />
                    {versions.length > 0 && (
                      <div className="w-0.5 h-full bg-border-secondary mt-1" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <button
                      onClick={onClearSelection}
                      className="text-left w-full group"
                    >
                      <div className="flex items-center gap-2">
                        <Text variant="text-sm" weight="semibold" color="primary" className="group-hover:text-utility-brand-800">
                          Current Version
                        </Text>
                        {!selectedTimestampMs && (
                          <span className="px-2 py-0.5 text-bg-v2-xs font-bg-v2-medium bg-utility-brand-200 text-utility-brand-800 rounded-bg-v2-sm">
                            Viewing
                          </span>
                        )}
                      </div>
                      <Text variant="text-xs" color="secondary" className="mt-1">
                        Active version shown to users
                      </Text>
                    </button>
                  </div>
                </div>
              </div>

              {/* Historical Versions */}
              {versions.map((version, index) => {
                const isSelected = selectedTimestampMs === version.timestamp_ms;

                return (
                  <div
                    key={version.timestamp_ms}
                    className={`px-bg-v2-lg py-bg-v2-md border-b border-border-secondary last:border-b-0 transition-colors ${isSelected
                      ? 'bg-utility-brand-100 border-l-4 border-l-utility-brand-500'
                      : 'hover:bg-bg-primary-hover border-l-4 border-l-transparent'
                      }`}
                  >
                    <div className="flex items-start gap-bg-v2-md">
                      <div className="flex flex-col items-center pt-1">
                        <div className={`w-3 h-3 rounded-full border-2 ${isSelected
                          ? 'bg-utility-brand-500 border-utility-brand-600'
                          : 'bg-bg-primary border-border-primary'
                          }`} />
                        {index < versions.length - 1 && (
                          <div className="w-0.5 h-full bg-border-secondary mt-1" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => {
                            onSelectVersion(version.timestamp_ms);
                            setIsOpen(false);
                          }}
                          className="text-left w-full group"
                        >
                          <div className="flex items-center gap-2">
                            <Text variant="text-sm" weight="semibold" color="primary" className="group-hover:text-utility-brand-800">
                              {formatVersionTimestamp(version.timestamp_ms)}
                            </Text>
                            {isSelected && (
                              <span className="px-2 py-0.5 text-bg-v2-xs font-bg-v2-medium bg-utility-brand-200 text-utility-brand-800 rounded-bg-v2-sm">
                                Viewing
                              </span>
                            )}
                          </div>
                        </button>

                        <IfHasPermission
                          permissions={permissions}
                          requires={Permissions.UpdateProjectPermission}
                        >
                          <UIButton
                            onClick={() => {
                              setIsOpen(false);
                              onRestoreVersion(version.timestamp_ms);
                            }}
                            disabled={isRestoring}
                            variant="outline"
                            size="small"
                            className="mt-2 w-full !justify-center !bg-utility-brand-100 hover:!bg-utility-brand-200 !border-utility-brand-600 !text-utility-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            label={
                              <div className="flex items-center gap-1.5">
                                <Icon name={IconName.Rotate90Degrees} size="xs" variant="secondary" />
                                Revert to this version
                              </div>
                            }
                          />
                        </IfHasPermission>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-bg-v2-lg py-bg-v2-md bg-bg-primary-alt border-t border-border-secondary">
              <Text variant="text-xs" color="secondary">
                💡 Click a version to preview it. Use "Revert" to restore it as the current version.
              </Text>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
